/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {NotificationRefreshIcon} from '../../icons/NotificationRefreshIcon';
import {SystemErrorIcon} from '../../icons/SystemErrorIcon';
import {dlsColor} from '../../theme/theme';
import {CTALink} from '../buttons/CTALink';

// defines the type definitions for error handler client...
export interface ErrorClientProps {
  refreshText?: string;
  omitRefresh?: boolean;
  onError?: (e: Error) => void;
  onRefresh?: Function;
  fallback?: React.ReactNode;
};

// defines the type definitions for error handler...
export interface ErrorHandlerProps extends ErrorClientProps {
  hasError?: boolean;
  errorText?: string;
};

// defines the styles used by error handler ...
export const useStyles = makeStyles(theme => ({
  root : {
    display        : 'flex',
    flexDirection  : 'column',
    alignItems     : 'center',
    justifyContent : 'center',
    fontFamily     : 'roboto',
    height         : '100%'
  },
  error : {
    margin    : '18px 6px',
    fontSize  : 16,
    color     : dlsColor('grey3'),
    textAlign : 'center'
  },
  refresh : {
    fontSize       : '16px',
    lineHeight     : '16px',
    fontStyle      : 'normal',
    fontWeight     : 700,
    letterSpacing  : '1px',
    textTransform  : 'uppercase',
    cursor         : 'pointer',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    color          : dlsColor('aaBlue'),
    width          : '100%',
    margin         : '12px 0'
  },
  refreshIcon : {
    margin : '1px 5px 0 0'
  }
}));

/**
 * Defines the Design Language System (DLS) Error Handler component.
 * https://www.figma.com/file/CpQzEOoxebEzEKNJm2FMmu/03_IXD-%26-IA?node-id=1%3A10782
 *
 * This component provides the option to user to retry when API fails.
 *
 * ```typescript
 *
 * import {ErrorHandler} from './ErrorHandler';
 *
 * <ErrorHandler hasError={hasError}
 *    onRefresh={onRefresh} />
 *
 * ```
 *
 * @param hasError  returns the component definition if true else returns null
 * @param onRefresh the API call to be retried
 * @param errorText the error message to be displayed
 */
export const ErrorHandler = ({
  hasError,
  onRefresh,
  omitRefresh = false,
  errorText = translate('common:errorHandler.defaultMessage'),
  refreshText = translate('common:errorHandler.refresh')
}: ErrorHandlerProps) => {
  const classes = useStyles();

  if (hasError) {
    return (
      <div className={classes.root}>
        <div><SystemErrorIcon stroke={dlsColor('grey3')} /></div>
        <div className={classes.error}>{errorText}</div>
        {!omitRefresh && (
          <CTALink onClick={onRefresh as React.MouseEventHandler} className={classes.refresh}>
            <div className={classes.refreshIcon}><NotificationRefreshIcon /></div>
            <div>{refreshText}</div>
          </CTALink>
        )}
      </div>
    );
  }
};

export default ErrorHandler;
